import type { LoginResponse, PublicTenantDto } from '@an-crm/shared';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import { BrandMark } from '@/components/brand/logo';
import { EASE_OUT } from '@/lib/motion';
import { ThemeToggleButton } from '@/app/shell/theme-toggle';
import { sessionStore } from '@/lib/auth/session-store';
import { applyBranding } from '@/lib/branding';
import { safeStorage } from '@/lib/storage';
import { type TokenResponse } from './api';
import { BrandHero } from './brand-hero';
import { CredentialsForm } from './credentials-form';
import { MfaSetupStep } from './mfa-setup-step';
import { MfaVerifyStep } from './mfa-verify-step';

export const LAST_TENANT_KEY = 'an-crm.lastTenant';

type Step =
  | { kind: 'credentials' }
  | { kind: 'mfa_verify'; challengeToken: string }
  | { kind: 'mfa_setup'; challengeToken: string };

export interface LoginPageProps {
  /** tenant slug from ?tenant= — falls back to the last used organisation */
  initialTenant?: string | undefined;
  onAuthenticated: () => void;
}

export function LoginPage({ initialTenant, onAuthenticated }: LoginPageProps) {
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [tenant, setTenant] = useState<PublicTenantDto | null>(null);
  const [startTenant] = useState(() => initialTenant ?? safeStorage.get(LAST_TENANT_KEY) ?? '');

  // preview the organisation's colours while signing in
  useEffect(() => {
    applyBranding(tenant?.branding ?? null);
  }, [tenant]);

  const finish = useCallback(
    (token: TokenResponse) => {
      sessionStore.setToken(token.accessToken, token.expiresIn);
      onAuthenticated();
    },
    [onAuthenticated],
  );

  const onCredentials = (result: LoginResponse, slug: string | null) => {
    if (slug) safeStorage.set(LAST_TENANT_KEY, slug);
    if (result.status === 'ok') finish(result);
    else if (result.status === 'mfa_required')
      setStep({ kind: 'mfa_verify', challengeToken: result.challengeToken });
    else setStep({ kind: 'mfa_setup', challengeToken: result.challengeToken });
  };

  const back = () => setStep({ kind: 'credentials' });
  const tenantName = tenant ? (tenant.branding.displayName ?? tenant.name) : undefined;

  return (
    <div className="grid min-h-dvh bg-bg lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
      <BrandHero tenantName={tenantName} />
      <main className="relative flex flex-col">
        <div className="flex items-center justify-between p-5 sm:p-8">
          <div className="flex items-center gap-2.5 lg:invisible">
            <BrandMark className="size-8" />
            <span className="font-display text-base font-semibold text-fg">Ankyah CRM</span>
          </div>
          <ThemeToggleButton />
        </div>
        <div className="flex flex-1 items-center justify-center px-5 pb-16 sm:px-8">
          <div className="w-full max-w-[400px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={step.kind}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25, ease: EASE_OUT }}
              >
                {step.kind === 'credentials' && (
                  <CredentialsForm
                    initialTenant={startTenant}
                    onResult={onCredentials}
                    onTenantResolved={setTenant}
                  />
                )}
                {step.kind === 'mfa_verify' && (
                  <MfaVerifyStep challengeToken={step.challengeToken} onVerified={finish} onBack={back} />
                )}
                {step.kind === 'mfa_setup' && (
                  <MfaSetupStep challengeToken={step.challengeToken} onVerified={finish} onBack={back} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <p className="pb-6 text-center text-xs text-fg-subtle">
          Protected by multi-factor authentication · Sessions are audited
        </p>
      </main>
    </div>
  );
}
